import {
  Box,
  Button,
  Grid,
  IconButton,
  Modal,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Tabs,
  Tab,
} from '@material-ui/core';
import Paper from '@material-ui/core/Paper';
import DeleteIcon from '@material-ui/icons/Delete';
import EventIcon from '@material-ui/icons/Event';
import MoreIcon from '@material-ui/icons/MoreVert';
import { AxiosResponse } from 'axios';
import { throttle } from 'lodash';
import moment from 'moment-timezone';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { CopyToClipboard } from '../../../components/CopyToClipboard';
import { ActiveStatusLabel, TargetTypeLabel } from '../../../components/Curation/Labels';
import { LinkedCurations, ScheduleSelect } from '../../../components/Curation/Popovers';
import { Priority } from '../../../components/Priority';
import StoryList from '../../../components/StoryList';
import useAlertDialog from '../../../hooks/useAlertDialog';
import useConfirmDialog from '../../../hooks/useConfirmDialog';
import useSnackbar from '../../../hooks/useSnackbar';
import api from '../../../services/api/base';
import { getCollection } from '../../../services/api/Collection';
import { deleteUserSegmentCuration, updateUserSegmentCuration } from '../../../services/api/Segment';
import {
  HomeType,
  ICollection,
  IFeaturedCard,
  IHomeCuration,
  ISegmentCuration,
  ISegmentCurationCollection,
  ISegmentCurationFeaturedCard,
  ISegmentCurationType,
  ISegmentCurationUpdateRequestDto,
  IStory,
  IUserSegment,
} from '../../../services/types';
import { convertFirstCharacterToUppercase, isFeaturedCard } from '../../../services/utils';
import { RouteNames } from '../../config';
import AddModal from '../featuredCard/Create/AddModal';
import { AbtestView } from './AbtestView';
import { AbtestInfoBox } from './Displays';
import { MoreMenu } from './MoreMenu';
import ObjectSelector from './ObjectSelector';
import useStyles from './styles';

interface IProps {
  homeType?: HomeType;
  segment: IUserSegment;
  mode: 'create' | 'edit' | 'view' | string;
  isEditing: boolean;
  featuredTitle?: string;
  featuredCards?: ISegmentCurationFeaturedCard[];
  collections?: ISegmentCurationCollection[];
  onChangeFeaturedTitle: (featuredTitle: string) => void;
  onChangeFeaturedCards: (featuredCards: ISegmentCuration[]) => void;
  onChangeCollections: (collections: ISegmentCuration[]) => void;
  onRefresh: () => void;
  onChangeHomeType: (_: any, homeType: HomeType) => void;
}

interface IScheduleEditData {
  type: 'featuredcard' | 'collection';
  index: number;
  key: 'startsAt' | 'endsAt';
  value: Date | null;
}

type FeaturedCardCurationType = ISegmentCurationType.AudioFeatured | ISegmentCurationType.FeaturedCards | ISegmentCurationType.TextFeatured;

type CollectionCurationType = ISegmentCurationType.AudioCollections | ISegmentCurationType.FictionSerials | ISegmentCurationType.TextCollections;

const CurationView = (props: IProps): JSX.Element => {
  const { segment } = props;
  const classes = useStyles();

  const snackbar = useSnackbar();
  const confirm = useConfirmDialog();
  const alert = useAlertDialog();

  // states
  const [isEditing, setIsEditing] = useState<boolean>(props.isEditing);
  const [featuredTitle, setFeaturedTitle] = useState<string>(props.featuredTitle || '');
  const [featuredCards, setFeaturedCards] = useState<ISegmentCurationFeaturedCard[]>(props.featuredCards || []);
  const [selectedFeaturedCardIds, setSelectedFeaturedCardIds] = useState<number[]>([]);
  const [collections, setCollections] = useState<ISegmentCurationCollection[]>(props.collections || []);
  const [selectedCollectionIds, setSelectedCollectionIds] = useState<number[]>([]);
  const [showFeaturedCardAddModalMode, setShowFeaturedCardAddModalMode] = useState<'create' | 'edit' | null>(null);
  const [modalFeaturedCard, setModalFeaturedCard] = useState<IFeaturedCard | undefined>();
  const [showAbtestModalMode, setShowAbtestModalMode] = useState<'create' | 'edit' | 'view' | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [anchorData, setAnchorData] = useState<null | ISegmentCuration[]>(null);
  const [anchorDataType, setAnchorDataType] = useState<ISegmentCurationType>();
  const [scheduleAnchor, setScheduleAnchor] = useState<null | HTMLElement>(null);
  const [scheduleEditData, setScheduleEditData] = useState<null | IScheduleEditData>(null);
  const [storyAnchorEl, setStoryAnchorEl] = useState<null | HTMLElement>(null);
  const [storyAnchorData, setStoryAnchorData] = useState<null | IStory[]>(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [refreshCounter, setRefreshCounter] = useState<number>(0);

  const [featuredCardType, setFeaturedCardType] = useState<FeaturedCardCurationType>(ISegmentCurationType.FeaturedCards);
  const [collectionType, setCollectionType] = useState<CollectionCurationType>(ISegmentCurationType.FictionSerials);

  // refs
  const menuFocusedRef = useRef<ISegmentCurationFeaturedCard | ISegmentCurationCollection>();
  const abtestTargetRef = useRef<ISegmentCurationFeaturedCard | ISegmentCurationCollection>();

  // API
  const fetchCollectionData = async (trackingId: string) => {
    try {
      const [fetchPromise, cancel] = getCollection(trackingId);
      const result = await fetchPromise;

      if (result && result.status === 200) {
        setStoryAnchorData(result.data.stories);
      }
    } catch (e) {
      if (api.isCancel(e)) return;
      console.error('e', e);
    } finally {
    }
  };
  const handleUpdateCuration = (curationId: number, data: ISegmentCurationUpdateRequestDto) => {
    return updateUserSegmentCuration(curationId, data);
  };

  const handleDeleteCuration = (curationId: number) => {
    return deleteUserSegmentCuration(curationId);
  };

  // utils
  const setStatesFromData = (data: IHomeCuration) => {};
  const makePriority = useCallback((items: ISegmentCurationFeaturedCard[] | ISegmentCurationCollection[]): number => {
    const baseNum = 2000;
    if (items.length === 0) {
      return baseNum;
    }
    const lastPriority = items[items.length - 1].priority;
    if (lastPriority < baseNum) {
      return baseNum;
    }
    return Math.floor(lastPriority / 10) * 10 + 10; // 마지막 번호 뒤로 10단위로 붙여줌
  }, []);

  // handlers
  const onSaveFeaturedCard = useCallback(() => {
    setShowFeaturedCardAddModalMode(null);
    setModalFeaturedCard(undefined);
    setRefreshCounter(refreshCounter + 1);
  }, []);
  const onRemoveFeaturedCard = (idx: number) => {
    setFeaturedCards(featuredCards.filter((c, i) => i !== idx));
    setSelectedFeaturedCardIds(selectedFeaturedCardIds.filter(fId => fId !== Number(featuredCards[idx].targetId)));
  };
  const onRemoveCollection = (idx: number) => {
    setCollections(collections.filter((c, i) => i !== idx));
    setSelectedCollectionIds(selectedCollectionIds.filter(fId => fId !== Number(collections[idx].targetId)));
  };
  const handleTitleChange = useCallback(
    throttle((v: string) => {
      props.onChangeFeaturedTitle(v);
    }, 1000),
    [],
  );
  const onChangePriority = useCallback(
    (type: 'collection' | 'featuredcard', idx: number, newPriority: number) => {
      const targetList = type === 'collection' ? collections : featuredCards;
      const newList = [...targetList];
      const targetId = newList[idx].id!;
      newList[idx] = { ...targetList[idx], priority: newPriority };
      const prioritySort = (
        a: ISegmentCurationCollection | ISegmentCurationFeaturedCard,
        b: ISegmentCurationCollection | ISegmentCurationFeaturedCard,
      ) => {
        if (a.priority < b.priority) {
          return -1;
        } else if (a.priority > b.priority) {
          return 1;
        } else {
          return a.id === targetId ? -1 : b.id === targetId ? 1 : 0;
        }
      };
      newList.sort(prioritySort);
      newList.forEach((item, idx) => {
        if (newList[idx - 1] !== undefined && newList[idx - 1].priority >= item.priority) {
          item.priority += 10;
        }
      });
      if (type === 'collection') {
        setCollections(newList as ISegmentCurationCollection[]);
      } else {
        setFeaturedCards(newList as ISegmentCurationFeaturedCard[]);
      }
    },
    [featuredCards, collections],
  );
  const onChangeDate = useCallback(
    (type: 'collection' | 'featuredcard', idx: number, key: string, newDate: Date) => {
      const targetList = type === 'collection' ? collections : featuredCards;
      const newList = [...targetList];
      newList[idx] = { ...targetList[idx], [key]: newDate };
      if (type === 'collection') {
        setCollections(newList as ISegmentCurationCollection[]);
      } else {
        setFeaturedCards(newList as ISegmentCurationFeaturedCard[]);
      }
    },
    [featuredCards, collections],
  );
  const onClickDateInput = (target: HTMLElement, editData: IScheduleEditData) => {
    if (isEditing) {
      setScheduleAnchor(target);
      setScheduleEditData(editData);
    }
  };
  const handleLinkedClick = (event: React.MouseEvent<HTMLButtonElement>, fc: ISegmentCuration) => {
    event.stopPropagation();
    event.preventDefault();
    if (fc.linkedCurations && fc.linkedCurations.length > 0) {
      setAnchorEl(event.currentTarget);
      setAnchorData(fc.linkedCurations);
      setAnchorDataType(fc.type);
    }
  };
  const handleStoryCountClick = (event: React.MouseEvent<HTMLButtonElement>, cl: ISegmentCurationCollection) => {
    event.stopPropagation();
    event.preventDefault();
    setStoryAnchorEl(event.currentTarget);
    fetchCollectionData(cl.object.trackingId!);
  };
  const handlePopoverClose = () => {
    setAnchorEl(null);
    setAnchorData(null);
  };
  const handleStoryPopoverClose = () => {
    setStoryAnchorEl(null);
    setStoryAnchorData(null);
  };
  const handleMoreMenuClick = (ele: HTMLElement, item: ISegmentCurationFeaturedCard | ISegmentCurationCollection) => {
    menuFocusedRef.current = item;
    setMenuAnchorEl(ele);
  };
  const handleMoreMenuClose = useCallback(() => {
    setMenuAnchorEl(null);
  }, [anchorEl]);

  const handleOnActivate = () => {
    requestAction(
      'Status change',
      `This will show the ${
        isFeaturedCard(menuFocusedRef.current!.type) ? 'Featured Card' : 'Collection'
      } on the segment's Home Curation. Are you sure you want to proceed?`,
      () => handleUpdateCuration(menuFocusedRef.current!.id!, { priority: menuFocusedRef.current!.priority, startsAt: new Date() }),
    );
  };
  const handleOnComplete = () => {
    const now = moment();
    const curAbTest = menuFocusedRef.current!.abTests![0];
    if (curAbTest && (curAbTest.endsAt === null || moment().isBefore(curAbTest.endsAt))) {
      alert.open({
        title: 'Alert',
        text: `This ${
          isFeaturedCard(menuFocusedRef.current!.type) ? 'Featured Card' : 'Collection'
        } is part of an active A/B test and cannot be completed.`,
      });
      return;
    }
    requestAction(
      'Status change',
      moment().isBefore(menuFocusedRef.current!.startsAt!)
        ? `This will cancel the scheduled Home Curation of the ${
            isFeaturedCard(menuFocusedRef.current!.type) ? 'Featured Card' : 'Collection'
          }. Are you sure you want to proceed?`
        : `This will no longer show the ${
            isFeaturedCard(menuFocusedRef.current!.type) ? 'Featured Card' : 'Collection'
          } on Home Curation. Are you sure you want to proceed?`,
      () =>
        handleUpdateCuration(menuFocusedRef.current!.id!, {
          priority: menuFocusedRef.current!.priority,
          endsAt: now.toDate(),
          startsAt: now.isSameOrBefore(menuFocusedRef.current!.startsAt) ? now.add(-10, 'seconds').toDate() : menuFocusedRef.current!.startsAt,
        }),
    );
  };
  const handleOnDelete = () => {
    requestAction(
      'Delete',
      `Are you sure you want to delete this ${isFeaturedCard(menuFocusedRef.current!.type) ? 'Featured Card' : 'Collection'}?`,
      () => handleDeleteCuration(menuFocusedRef.current!.id!),
    );
  };
  const requestAction = (title: string, text: string, res: () => Promise<AxiosResponse>) => {
    confirm.open({
      title: title,
      text: text,
      onProceed: () => {
        res()
          .then(() => {
            // fetchListData(page, rowsPerPage);
            props.onRefresh();
            setMenuAnchorEl(null);
          })
          .catch(e => {
            console.log(e);
            snackbar.error(e);
          });
      },
    });
  };
  const handleClickCreateAbtest = (item: ISegmentCurationFeaturedCard | ISegmentCurationCollection) => {
    abtestTargetRef.current = item;
    setShowAbtestModalMode('create');
  };
  const handleClickEditAbtest = (item: ISegmentCurationFeaturedCard | ISegmentCurationCollection) => {
    abtestTargetRef.current = item;
    setShowAbtestModalMode('view');
  };
  const onAddFeaturedCard = useCallback(
    (item: IFeaturedCard) => {
      setFeaturedCards(
        featuredCards.concat({
          segmentId: segment.id!,
          type: featuredCardType,
          targetId: item.id!.toString(),
          priority: makePriority(featuredCards),
          startsAt: moment().toDate(),
          endsAt: null,
          object: item as IFeaturedCard,
          linkedCurations: item.linkedCurations,
        }),
      );
    },
    [featuredCards, featuredCardType],
  );
  const onAddCollection = useCallback(
    (item: ICollection) => {
      setCollections(
        collections.concat({
          segmentId: segment.id!,
          type: collectionType,
          targetId: item.trackingId!.toString(),
          priority: makePriority(collections),
          startsAt: moment().toDate(),
          endsAt: null,
          object: item as ICollection,
          linkedCurations: item.linkedCurations,
        }),
      );
    },
    [collections, collectionType],
  );
  const onSaveAbtest = useCallback(() => {
    setShowAbtestModalMode(null);
    props.onRefresh();
  }, []);

  // watches

  useEffect(() => {
    switch (props.homeType) {
      case HomeType.ALL:
        setFeaturedCardType(ISegmentCurationType.FeaturedCards);
        setCollectionType(ISegmentCurationType.FictionSerials);
        break;
      case HomeType.LISTEN:
        setFeaturedCardType(ISegmentCurationType.AudioFeatured);
        setCollectionType(ISegmentCurationType.AudioCollections);
        break;
      case HomeType.READ:
        setFeaturedCardType(ISegmentCurationType.TextFeatured);
        setCollectionType(ISegmentCurationType.TextCollections);
        break;
    }
  }, [props.homeType]);

  useEffect(() => {
    setIsEditing(props.isEditing);
    if (props.mode === 'view') {
      // initialize
      setFeaturedTitle(props.featuredTitle || '');
      setFeaturedCards(props.featuredCards || []);
      setCollections(props.collections || []);
    }
  }, [props.mode, props.isEditing]);

  useEffect(() => {
    setFeaturedCards(props.featuredCards!);
    setCollections(props.collections!);
  }, [props.featuredCards, props.collections]);

  useEffect(() => {
    setSelectedFeaturedCardIds(featuredCards.map(item => item.object.id!));
    props.onChangeFeaturedCards(
      featuredCards.map(
        (item): ISegmentCuration => {
          return {
            id: item.id,
            segmentId: segment.id,
            type: item.type,
            targetId: item.targetId,
            priority: item.priority,
            startsAt: item.startsAt,
            endsAt: item.endsAt,
          };
        },
      ),
    );
  }, [featuredCards]);

  useEffect(() => {
    setSelectedCollectionIds(collections.map(item => item.object.id!));
    props.onChangeCollections(
      collections.map(
        (item): ISegmentCuration => {
          return {
            id: item.id,
            segmentId: segment.id,
            type: item.type,
            targetId: item.targetId,
            priority: item.priority,
            startsAt: item.startsAt,
            endsAt: item.endsAt,
          };
        },
      ),
    );
  }, [collections]);

  // init return
  if (!segment) {
    return <div></div>;
  }

  return (
    <Box>
      <Paper className={`${classes.paper} ${classes.segmentInfoBox}`}>
        <div style={{ display: 'flex', marginBottom: 30 }}>
          <div className={classes.segmentInfoItem}>Segment ID</div>
          <div className={classes.segmentInfoItem}>{segment.id}</div>
          <div className={classes.segmentInfoItem}>Segment Type</div>
          <div className={classes.segmentInfoItem} style={{ textTransform: 'capitalize' }}>
            {segment.type}
          </div>
        </div>
        <div style={{ display: 'flex' }}>
          <div className={classes.segmentInfoItem}>Segment Title</div>
          <div>{segment.name}</div>
        </div>
      </Paper>
      <Tabs value={props.homeType} onChange={props.onChangeHomeType}>
        <Tab label={`Default Home`} value={HomeType.ALL} />
        <Tab label={`Read Home`} value={HomeType.READ} />
        <Tab label={`Listen Home`} value={HomeType.LISTEN} />
      </Tabs>
      <Paper className={classes.paper} style={{ marginTop: '20px' }}>
        <Grid container>
          <Grid item xs={12} style={{ display: 'flex', marginBottom: '20px' }}>
            <Typography variant="subtitle1" style={{ marginRight: 20 }}>
              Featured Title
            </Typography>
            <TextField
              placeholder="Featured Title"
              style={{ width: 345 }}
              defaultValue={featuredTitle}
              disabled={!isEditing}
              onChange={e => handleTitleChange(e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle1">Featured Card</Typography>
          </Grid>
          <Grid container className={classes.dualTableWrap}>
            <Grid item className={isEditing ? classes.dualTableLeftWrap : classes.dualTableLeftWrapExpand}>
              <Table component="table" className={classes.lastNoBorder}>
                <TableHead component="thead">
                  <TableRow component="tr" className={classes.dualTableLeftHeader}>
                    <TableCell component="th" align="center" className={classes.cellStatus}>
                      Status
                    </TableCell>

                    <TableCell component="th" align="center" className={classes.cellLinked}>
                      Linked
                    </TableCell>
                    <TableCell component="th" align="center" className={classes.cellPriority}>
                      Priority
                    </TableCell>
                    <TableCell component="th" align="center" className={classes.cellId}>
                      ID
                    </TableCell>
                    <TableCell component="th" align="center" className={classes.cellCover}>
                      Cover
                    </TableCell>
                    <TableCell component="th">Title</TableCell>
                    <TableCell component="th">Countries</TableCell>
                    <TableCell component="th" align="center">
                      Content Type
                    </TableCell>
                    <TableCell component="th" align="center" className={classes.cellDateTime}>
                      Start
                    </TableCell>
                    <TableCell component="th" align="center" className={classes.cellDateTime}>
                      End
                    </TableCell>
                    {!isEditing && (
                      <TableCell component="th" align="center" className={classes.cellAbtestId}>
                        A/B Test Id
                      </TableCell>
                    )}
                    {!isEditing && (
                      <TableCell component="th" align="center" className={classes.cellId}>
                        A/B Test
                      </TableCell>
                    )}
                    {isEditing && (
                      <TableCell component="th" align="center" className={classes.cellDelete}>
                        Delete
                      </TableCell>
                    )}
                    {!isEditing && <TableCell component="th" className={classes.cellMore}></TableCell>}
                  </TableRow>
                </TableHead>
                {featuredCards.map((fc, idx) => (
                  <TableBody component="tbody" key={`fc-list-item-${fc.targetId}`} className={classes.tbodyWithAbTest}>
                    <TableRow component="tr" className={(!isEditing && fc.abTests && fc.abTests!.length > 0 && classes.trWithAbTest) || undefined}>
                      <TableCell component="td" align="center">
                        <ActiveStatusLabel startsAt={fc.startsAt} endsAt={fc.endsAt} />
                      </TableCell>

                      <TableCell component="td" align="center">
                        <span
                          className={classes.linkCountText}
                          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                            handleLinkedClick(e, fc);
                          }}
                        >
                          {fc.linkedCurations?.length || 0}
                        </span>
                      </TableCell>
                      <TableCell component="td" align="center">
                        <Priority
                          priority={fc.priority}
                          onEnterValue={(priority: number) => {
                            if (priority !== fc.priority) {
                              onChangePriority('featuredcard', idx, priority);
                            }
                          }}
                          onFocus={() => {}}
                          isAlerted={fc.isAlerted}
                          errorMessage={fc.errorMessage}
                          readOnly={!isEditing}
                        />
                      </TableCell>
                      <TableCell component="td" align="center">
                        {fc.object.id}
                      </TableCell>
                      <TableCell component="td" align="center">
                        <div
                          style={{
                            backgroundImage: `url(${process.env.CDN_URL}/${fc.object.displayCoverUrl || fc.object.coverUrl}?w=320)`,
                          }}
                          className={classes.coverImageSmall}
                        />
                      </TableCell>
                      <TableCell component="td">
                        <Box display="flex">
                          <Box display="flex" flexDirection="column" alignContent="center" alignItems="flex-start">
                            <Box>
                              <TargetTypeLabel type={fc.object!.type} />
                            </Box>
                            <Box>{fc.abTests && fc.abTests.length > 0 && <TargetTypeLabel type={'A/B'} />}</Box>
                          </Box>
                          <Box>
                            <span
                              className={`${classes.popoverLink} ${classes.twoLine}`}
                              onClick={() => {
                                setShowFeaturedCardAddModalMode('edit');
                                setModalFeaturedCard(fc.object);
                              }}
                            >
                              {fc.object.title}
                            </span>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell component="td" align="center">
                        {fc.object.availableCountryCountText || '-'}
                      </TableCell>
                      <TableCell component="td" align="center">
                        {fc.object.storyTypes?.map(storyType => convertFirstCharacterToUppercase(storyType)).join(', ')}
                      </TableCell>
                      <TableCell component="td" align="center">
                        {isEditing ? (
                          <TextField
                            InputProps={{
                              endAdornment: <EventIcon />,
                              readOnly: true,
                            }}
                            disabled={!isEditing}
                            value={fc.startsAt ? moment(fc.startsAt).format('lll') : ''}
                            className={classes.dateInput}
                            onClick={e => {
                              onClickDateInput(e.currentTarget, {
                                type: 'featuredcard',
                                index: idx,
                                key: 'startsAt',
                                value: fc.startsAt !== null ? moment(fc.startsAt).toDate() : null,
                              });
                            }}
                          />
                        ) : (
                          <span className={classes.nowrap}>{fc.startsAt ? moment(fc.startsAt).format('lll') : ''}</span>
                        )}
                      </TableCell>
                      <TableCell component="td" align="center">
                        {isEditing ? (
                          <TextField
                            InputProps={{
                              endAdornment: <EventIcon />,
                              readOnly: true,
                            }}
                            disabled={!isEditing}
                            value={fc.endsAt ? moment(fc.endsAt).format('lll') : ''}
                            className={classes.dateInput}
                            onClick={e => {
                              onClickDateInput(e.currentTarget, {
                                type: 'featuredcard',
                                index: idx,
                                key: 'endsAt',
                                value: fc.endsAt !== null ? moment(fc.endsAt).toDate() : null,
                              });
                            }}
                          />
                        ) : (
                          <span className={classes.nowrap}>{fc.endsAt ? moment(fc.endsAt).format('lll') : ''}</span>
                        )}
                      </TableCell>
                      {!isEditing && (
                        <TableCell component="td" align="center">
                          {fc.abTests && fc.abTests!.length > 0 ? fc.abTests![0].id : '-'}
                        </TableCell>
                      )}
                      {!isEditing && fc.abTests && fc.abTests!.length > 0 && (
                        <TableCell component="td" align="center">
                          <Button
                            variant="outlined"
                            size="small"
                            className={classes.greyButton}
                            onClick={() => {
                              handleClickEditAbtest(fc);
                            }}
                          >
                            Edit
                          </Button>
                        </TableCell>
                      )}
                      {!isEditing && (!fc.abTests || fc.abTests!.length === 0) && (
                        <TableCell component="td" align="center">
                          <Button
                            variant="outlined"
                            color="primary"
                            size="small"
                            onClick={() => {
                              handleClickCreateAbtest(fc);
                            }}
                          >
                            Create
                          </Button>
                        </TableCell>
                      )}
                      {isEditing && (
                        <TableCell component="td" align="center">
                          <IconButton
                            onClick={() => {
                              onRemoveFeaturedCard(idx);
                            }}
                          >
                            <DeleteIcon color="primary" />
                          </IconButton>
                        </TableCell>
                      )}
                      {!isEditing && (
                        <TableCell component="td" align="center" className={classes.cellMore}>
                          <IconButton
                            size="small"
                            style={{}}
                            onClick={e => {
                              handleMoreMenuClick(e.currentTarget, fc);
                            }}
                          >
                            <MoreIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      )}
                    </TableRow>
                    {!isEditing && fc.abTests && fc.abTests!.length > 0 && (
                      <TableRow component="tr" className={classes.trOfAbTest}>
                        <TableCell component="td" colSpan={4} />
                        <TableCell component="td" colSpan={8}>
                          <AbtestInfoBox abtestItem={fc.abTests![0]} />
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                ))}
              </Table>
            </Grid>
            {isEditing && (
              <Grid item className={classes.dualTableRightWrap}>
                <Box className={classes.dualTableRightBox}>
                  <Box className={classes.dualTableRightHeader} style={{ position: 'relative', zIndex: 1 }}>
                    <Typography component="span" className={classes.dualTableRightHeaderText}>
                      Add Featured Cards
                    </Typography>
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      style={{ position: 'absolute', right: 13, top: 10 }}
                      onClick={() => setShowFeaturedCardAddModalMode('create')}
                    >
                      Create
                    </Button>
                  </Box>
                  <ObjectSelector
                    type="featuredcard"
                    className={classes.dualTableRightContent}
                    selectedIds={selectedFeaturedCardIds}
                    segment={segment}
                    selectLimit={25}
                    onAddFeaturedCard={onAddFeaturedCard}
                    refreshCounter={refreshCounter}
                  />
                </Box>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Paper>
      <Paper className={classes.paper}>
        <Grid container>
          <Grid item xs={12}>
            <Typography variant="subtitle1">Collections</Typography>
          </Grid>
          <Grid container className={classes.dualTableWrap}>
            <Grid item className={isEditing ? classes.dualTableLeftWrap : classes.dualTableLeftWrapExpand}>
              <Table component="table" className={classes.lastNoBorder}>
                <TableHead component="thead">
                  <TableRow component="tr" className={classes.dualTableLeftHeader}>
                    <TableCell component="th" align="center" className={classes.cellStatus}>
                      Status
                    </TableCell>

                    <TableCell component="th" align="center" className={classes.cellLinked}>
                      Linked
                    </TableCell>
                    <TableCell component="th" align="center" className={classes.cellPriority}>
                      Priority
                    </TableCell>
                    <TableCell component="th" align="center" className={classes.cellId}>
                      ID
                    </TableCell>
                    <TableCell component="th">Title</TableCell>
                    <TableCell component="th" align="center" className={classes.cellStories}>
                      Countries
                    </TableCell>
                    <TableCell component="th" align="center" className={classes.cellStories}>
                      Stories
                    </TableCell>
                    <TableCell component="th" className={`${classes.limitLine} ${classes.cellTrackingId}`}>
                      <span>
                        Tracking
                        <br />
                        ID
                      </span>
                    </TableCell>
                    <TableCell component="th" align="center">
                      Content Type
                    </TableCell>
                    <TableCell component="th" align="center" className={classes.cellDateTime}>
                      Start
                    </TableCell>
                    <TableCell component="th" align="center" className={classes.cellDateTime}>
                      End
                    </TableCell>
                    {!isEditing && (
                      <TableCell component="th" align="center" className={classes.cellAbtestId}>
                        A/B Test Id
                      </TableCell>
                    )}
                    {!isEditing && (
                      <TableCell component="th" align="center" className={classes.cellId}>
                        A/B Test
                      </TableCell>
                    )}
                    {isEditing && (
                      <TableCell component="th" align="center" className={classes.cellDelete}>
                        Delete
                      </TableCell>
                    )}
                    {!isEditing && <TableCell component="th" className={classes.cellMore}></TableCell>}
                  </TableRow>
                </TableHead>
                {collections.map((cl, idx) => (
                  <TableBody component="tbody" key={`collection-item-list-${cl.targetId}`} className={classes.tbodyWithAbTest}>
                    <TableRow component="tr" className={(!isEditing && cl.abTests && cl.abTests!.length > 0 && classes.trWithAbTest) || undefined}>
                      <TableCell component="td" align="center">
                        <ActiveStatusLabel startsAt={cl.startsAt} endsAt={cl.endsAt} />
                      </TableCell>

                      <TableCell component="td" align="center">
                        <span
                          className={classes.linkCountText}
                          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                            handleLinkedClick(e, cl);
                          }}
                        >
                          {cl.linkedCurations?.length || 0}
                        </span>
                      </TableCell>
                      <TableCell component="td" align="center">
                        <Priority
                          priority={cl.priority}
                          onEnterValue={(priority: number) => {
                            if (priority !== cl.priority) {
                              onChangePriority('collection', idx, priority);
                            }
                          }}
                          onFocus={() => {}}
                          isAlerted={cl.isAlerted}
                          errorMessage={cl.errorMessage}
                          readOnly={!isEditing}
                        />
                      </TableCell>
                      <TableCell component="td" align="center">
                        {cl.object.id}
                      </TableCell>
                      <TableCell component="td">
                        <Link
                          to={`${RouteNames.Collection}/v/${cl.object.trackingId!}`}
                          onClick={e => {
                            e.stopPropagation();
                          }}
                          target="_blank"
                          className={`${classes.popoverLink} ${classes.curationScheduleListTitleCollection}`}
                        >
                          {cl.object.internalTitle && <div className={`${classes.internalTitleEmphasize}`}>{cl.object.internalTitle}</div>}
                          <div>{cl.object.title}</div>
                        </Link>
                      </TableCell>
                      <TableCell component="td" align="center">
                        {cl.object.availableCountryCountText || '-'}
                      </TableCell>
                      <TableCell component="td" align="center">
                        <span
                          className={classes.popoverOpener}
                          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                            handleStoryCountClick(e, cl);
                          }}
                        >
                          {cl.object.storyCount}
                        </span>
                      </TableCell>
                      <TableCell component="td" align="center">
                        <CopyToClipboard
                          content={cl.object.trackingId || ''}
                          style={{ maxWidth: 120, overflowWrap: 'break-word', display: 'inline-block' }}
                        />
                      </TableCell>
                      <TableCell component="td" align="center">
                        {cl.object.storyTypes?.map(storyType => convertFirstCharacterToUppercase(storyType)).join(', ')}
                      </TableCell>
                      <TableCell component="td" align="center">
                        {isEditing ? (
                          <TextField
                            InputProps={{
                              endAdornment: <EventIcon />,
                              readOnly: true,
                            }}
                            disabled={!isEditing}
                            value={cl.startsAt ? moment(cl.startsAt).format('lll') : ''}
                            className={classes.dateInput}
                            onClick={e => {
                              onClickDateInput(e.currentTarget, {
                                type: 'collection',
                                index: idx,
                                key: 'startsAt',
                                value: cl.startsAt !== null ? moment(cl.startsAt).toDate() : null,
                              });
                            }}
                          />
                        ) : (
                          <span className={classes.nowrap}>{cl.startsAt ? moment(cl.startsAt).format('lll') : ''}</span>
                        )}
                      </TableCell>
                      <TableCell component="td" align="center">
                        {isEditing ? (
                          <TextField
                            InputProps={{
                              endAdornment: <EventIcon />,
                              readOnly: true,
                            }}
                            disabled={!isEditing}
                            value={cl.endsAt ? moment(cl.endsAt).format('lll') : ''}
                            className={classes.dateInput}
                            onClick={e => {
                              onClickDateInput(e.currentTarget, {
                                type: 'collection',
                                index: idx,
                                key: 'endsAt',
                                value: cl.endsAt !== null ? moment(cl.endsAt).toDate() : null,
                              });
                            }}
                          />
                        ) : (
                          <span className={classes.nowrap}>{cl.endsAt ? moment(cl.endsAt).format('lll') : ''}</span>
                        )}
                      </TableCell>
                      {!isEditing && (
                        <TableCell component="td" align="center">
                          {cl.abTests && cl.abTests!.length > 0 ? cl.abTests![0].id : '-'}
                        </TableCell>
                      )}
                      {!isEditing && cl.abTests && cl.abTests!.length > 0 && (
                        <TableCell component="td" align="center">
                          <Button
                            variant="outlined"
                            size="small"
                            className={classes.greyButton}
                            onClick={() => {
                              handleClickEditAbtest(cl);
                            }}
                          >
                            Edit
                          </Button>
                        </TableCell>
                      )}
                      {!isEditing && (!cl.abTests || cl.abTests!.length === 0) && (
                        <TableCell component="td" align="center">
                          <Button
                            variant="outlined"
                            color="primary"
                            size="small"
                            onClick={() => {
                              handleClickCreateAbtest(cl);
                            }}
                          >
                            Create
                          </Button>
                        </TableCell>
                      )}
                      {isEditing && (
                        <TableCell component="td" align="center">
                          <IconButton
                            onClick={() => {
                              onRemoveCollection(idx);
                            }}
                          >
                            <DeleteIcon color="primary" />
                          </IconButton>
                        </TableCell>
                      )}
                      {!isEditing && (
                        <TableCell component="td" align="center" className={classes.cellMore}>
                          <IconButton
                            size="small"
                            style={{}}
                            onClick={e => {
                              handleMoreMenuClick(e.currentTarget, cl);
                            }}
                          >
                            <MoreIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      )}
                    </TableRow>
                    {!isEditing && cl.abTests && cl.abTests!.length > 0 && (
                      <TableRow component="tr" className={classes.trOfAbTest}>
                        <TableCell component="td" colSpan={4} style={{ borderBottom: 'none' }} />
                        <TableCell component="td" colSpan={8} style={{ borderBottom: 'none' }}>
                          <AbtestInfoBox abtestItem={cl.abTests![0]} />
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                ))}
              </Table>
            </Grid>
            {isEditing && (
              <Grid item className={classes.dualTableRightWrap} style={{ flexBasis: 322 }}>
                <Box className={classes.dualTableRightBox}>
                  <Box className={classes.dualTableRightHeader} style={{ position: 'relative', zIndex: 1 }}>
                    <Typography component="span" className={classes.dualTableRightHeaderText}>
                      Add Collections
                    </Typography>
                  </Box>
                  <ObjectSelector
                    type="collection"
                    className={classes.dualTableRightContent}
                    selectedIds={selectedCollectionIds}
                    segment={segment}
                    selectLimit={50}
                    onAddCollection={onAddCollection}
                  />
                </Box>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Paper>
      {showFeaturedCardAddModalMode !== null && (
        <Modal
          open={true}
          onEscapeKeyDown={() => {
            setShowFeaturedCardAddModalMode(null);
            setModalFeaturedCard(undefined);
          }}
        >
          <div>
            <AddModal
              onSave={onSaveFeaturedCard}
              onClose={() => {
                setShowFeaturedCardAddModalMode(null);
                setModalFeaturedCard(undefined);
              }}
              mode={showFeaturedCardAddModalMode}
              featuredCard={modalFeaturedCard}
              featuredCardId={modalFeaturedCard ? modalFeaturedCard.id! : undefined}
            />
          </div>
        </Modal>
      )}
      {anchorData && (
        <Popover
          id={`children-popover-linked-curations`}
          open={true}
          anchorEl={anchorEl}
          onClose={handlePopoverClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <LinkedCurations linkedCurations={anchorData} type={isFeaturedCard(anchorDataType) ? 'featuredcard' : 'collection'} />
        </Popover>
      )}
      {storyAnchorEl && storyAnchorData && (
        <Popover
          id={`story-list-popover`}
          open={true}
          anchorEl={storyAnchorEl}
          onClose={handleStoryPopoverClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <Box maxWidth={340} maxHeight={357} overflow="auto">
            <StoryList hideSearch={true} readonly={true} stories={storyAnchorData || []} additionalInfo={true} />
          </Box>
        </Popover>
      )}
      {scheduleAnchor && scheduleEditData && (
        <Popover
          id={`schedule-popover`}
          open={true}
          anchorEl={scheduleAnchor}
          onClose={handlePopoverClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <ScheduleSelect
            type={scheduleEditData.key}
            value={scheduleEditData!.value}
            onChangeDate={value => {
              onChangeDate(scheduleEditData.type, scheduleEditData.index, scheduleEditData.key, value as Date);
              setScheduleAnchor(null);
              setScheduleEditData(null);
            }}
            onCancel={() => {
              setScheduleAnchor(null);
              setScheduleEditData(null);
            }}
          />
        </Popover>
      )}
      {menuAnchorEl && (
        <MoreMenu
          anchorEl={menuAnchorEl}
          item={menuFocusedRef.current!}
          onClose={handleMoreMenuClose}
          onActivate={() => {
            handleOnActivate();
          }}
          onComplete={() => {
            handleOnComplete();
          }}
          onDelete={() => {
            handleOnDelete();
          }}
        />
      )}
      {showAbtestModalMode !== null && (
        <Modal
          open={true}
          onEscapeKeyDown={() => {
            setShowAbtestModalMode(null);
          }}
        >
          <div>
            <AbtestView
              mode={showAbtestModalMode}
              targetCuration={abtestTargetRef.current!}
              segment={segment}
              onClose={() => {
                setShowAbtestModalMode(null);
              }}
              onSave={onSaveAbtest}
            />
          </div>
        </Modal>
      )}
    </Box>
  );
};

export default CurationView;
